/**
 * Route guard: gates children behind a specific role name.
 *
 * Must be nested inside <RequireAuth>, which guarantees a user is
 * present. If the user lacks the required role, render an inline
 * access-denied panel rather than redirecting — it's friendlier to
 * the SPA user who navigated in via a link they didn't realize was
 * gated.
 */

import { Block } from '@mui/icons-material';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './useAuth';

interface RequireRoleProps {
  role: string;
  children: ReactNode;
}

export function RequireRole({ role, children }: RequireRoleProps) {
  const { user } = useAuth();

  // RequireAuth runs first, so `user` is guaranteed non-null in the
  // happy path. Defensive null-check satisfies the type system.
  if (!user) return null;

  // Admin role is a wildcard — admins implicitly satisfy any role
  // check. Matches the backend's require_role semantics so the
  // SPA gates and the API gates agree.
  const isAdmin = user.roles.some((r) => r.name === 'admin');
  const has = isAdmin || user.roles.some((r) => r.name === role);
  if (!has) {
    return <AccessDenied role={role} />;
  }

  return <>{children}</>;
}

function AccessDenied({ role }: { role: string }) {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: '100%',
          p: 5,
          border: '1px solid',
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Block sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography variant="h4">Access restricted</Typography>
          <Typography variant="body1" color="text.secondary">
            You need the <strong>{role}</strong> role to view this section. Ask an administrator to
            grant it to your account.
          </Typography>
          <Button component={Link} to="/" variant="outlined" size="large">
            Back to home
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
